import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';

import {
  UpdateCustomsEntryBIZcoreTD
} from '../../../../../redux/actions/business/core/customsEntriesActions';
import {
  GetBrokersDropdownBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import {
  CLEAR_BROKERS_DROPDOWN_BIZ_CORE_TD
} from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const MODES = { EDIT: 2 };

const Data = {
  Broker: null,
  EntryDate: "",
  IsActiveVersion: true,
  TotalRecoverable: "",
  TotalDeficiency: "",
};

const CustomsEntriesModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.EDIT);

  const {
    results: brokerResults,
    loading: brokerSearchLoading,
    handleSearch: handleBrokerSearch,
    reset: resetBrokerSearch,
  } = useSearchableSelect(
    GetBrokersDropdownBIZcoreTD,
    (state) => {
      const list = state["business.core.tradeDefence"].brokersDropdownList;
      if (list?.records && Array.isArray(list.records)) return list.records;
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.EDIT);
    dispatch({ type: CLEAR_BROKERS_DROPDOWN_BIZ_CORE_TD });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    try {
      if (open) {
        resetBrokerSearch();
        if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            Broker: update.ref_broker_id || null,
            EntryDate: update.entry_date || "",
            IsActiveVersion: update.is_active_version !== undefined ? update.is_active_version : true,
            TotalRecoverable: update.total_recoverable ?? "",
            TotalDeficiency: update.total_deficiency ?? "",
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    const obj = {
      ref_broker_id: formData.Broker?.id ?? formData.Broker,
      entry_date: formData.EntryDate || null,
      is_active_version: formData.IsActiveVersion,
      total_recoverable: formData.TotalRecoverable !== "" ? Number(formData.TotalRecoverable) : null,
      total_deficiency: formData.TotalDeficiency !== "" ? Number(formData.TotalDeficiency) : null,
    };
    if (mode === MODES.EDIT) {
      dispatch(UpdateCustomsEntryBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"} sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* Broker */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(brokerResults) ? brokerResults : []}
                loading={brokerSearchLoading}
                value={
                  formData.Broker
                  ? (Array.isArray(brokerResults) ? brokerResults : []).find(b => b.id === (formData.Broker?.id ?? formData.Broker)) || formData.Broker
                  : null
                }
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') handleBrokerSearch(value);
                }}
                onChange={(e, value) => setFormData({ ...formData, Broker: value || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : (option.name || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') return option.id === value;
                  return option.id === value?.id;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2">{option.name || option.id || "NA"}</Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Broker"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {brokerSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Entry Date */}
            <Box mb={"15px"}>
              <TextField
                label="Entry date"
                fullWidth
                name="EntryDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.EntryDate}
                onChange={handleChange}
              />
            </Box>

            {/* Is Active Version */}
            <Box mb={"15px"}>
              <TextField
                select
                label="Is active version"
                fullWidth
                name="IsActiveVersion"
                value={formData.IsActiveVersion}
                onChange={handleChange}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </TextField>
            </Box>

            {/* Total Recoverable */}
            <Box mb={"15px"}>
              <TextField
                label="Total recoverable"
                fullWidth
                name="TotalRecoverable"
                type="number"
                value={formData.TotalRecoverable}
                onChange={handleChange}
              />
            </Box>

            {/* Total Deficiency */}
            <Box mb={"15px"}>
              <TextField
                label="Total deficiency"
                fullWidth
                name="TotalDeficiency"
                type="number"
                value={formData.TotalDeficiency}
                onChange={handleChange}
              />
            </Box>
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CustomsEntriesModal;
